import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Pagination,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import ReactMarkdown from 'react-markdown';

// 나만의 용어 목록 컴포넌트
const MyTermsList = () => {
  const [loading, setLoading] = useState(true);
  const [myTerms, setMyTerms] = useState([]);
  const [myDefinitions, setMyDefinitions] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [page, setPage] = useState(1);
  const [filteredItems, setFilteredItems] = useState([]);
  const itemsPerPage = 10;
  const { user } = useAuth();
  
  // 삭제 관련 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // 수정 관련 상태
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [editedDefinition, setEditedDefinition] = useState('');
  const [editedKoreanDefinition, setEditedKoreanDefinition] = useState('');
  
  // 알림 상태
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 데이터 로드
  const loadData = async () => {
    setLoading(true);
    try {
      if (!user) return;
      
      // 나만의 용어 로드
      const { data: termsData, error: termsError } = await supabase
        .from('my_terms')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (termsError) throw termsError;
      setMyTerms(termsData || []);

      // 나만의 정의 로드
      const { data: defsData, error: defsError } = await supabase
        .from('my_definitions')
        .select('*, terms:term_id(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (defsError) throw defsError;
      setMyDefinitions(defsData || []);

    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setSnackbar({
        open: true,
        message: '데이터를 불러오는데 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  // 검색 필터링 및 페이지네이션
  useEffect(() => {
    const currentData = tabValue === 0 ? myTerms : myDefinitions;
    let filtered = currentData;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (tabValue === 0) {
        filtered = myTerms.filter(term => 
          term.term.toLowerCase().includes(query) || 
          (term.definition && term.definition.toLowerCase().includes(query)) ||
          (term.korean_definition && term.korean_definition.toLowerCase().includes(query)) ||
          (term.subject && term.subject.toLowerCase().includes(query)) ||
          (term.keywords && term.keywords.some(kw => kw.toLowerCase().includes(query)))
        );
      } else {
        filtered = myDefinitions.filter(def => 
          (def.terms?.term && def.terms.term.toLowerCase().includes(query)) || 
          (def.custom_definition && def.custom_definition.toLowerCase().includes(query)) ||
          (def.korean_definition && def.korean_definition.toLowerCase().includes(query)) ||
          (def.terms?.subject && def.terms.subject.toLowerCase().includes(query)) ||
          (def.keywords && def.keywords.some(kw => kw.toLowerCase().includes(query)))
        );
      }
    }
    
    setFilteredItems(filtered);
    setPage(1); // 검색시 첫 페이지로 리셋
  }, [searchQuery, tabValue, myTerms, myDefinitions]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setExpandedItem(null);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    setExpandedItem(null); // 페이지 변경시 확장된 항목 초기화
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleExpandToggle = (id) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  // 페이지네이션을 위한 현재 페이지 항목들
  const paginatedItems = filteredItems.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const pageCount = Math.ceil(filteredItems.length / itemsPerPage);

  // 삭제 다이얼로그 열기
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };
  
  // 삭제 취소
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };
  
  // 삭제 확인
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      const table = tabValue === 0 ? 'my_terms' : 'my_definitions';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', itemToDelete.id);
        
      if (error) throw error;
      
      setSnackbar({
        open: true,
        message: '성공적으로 삭제되었습니다.',
        severity: 'success'
      });
      
      // 데이터 다시 로드
      loadData();
    } catch (error) {
      console.error('삭제 실패:', error);
      setSnackbar({
        open: true,
        message: '삭제 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  // 수정 다이얼로그 열기
  const handleEditClick = (item) => {
    setItemToEdit(item);
    setEditedDefinition(tabValue === 0 ? item.definition : item.custom_definition);
    setEditedKoreanDefinition(item.korean_definition || '');
    setEditDialogOpen(true);
  };
  
  // 수정 취소
  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setItemToEdit(null);
    setEditedDefinition('');
    setEditedKoreanDefinition('');
  };
  
  // 수정 확인
  const handleEditConfirm = async () => {
    if (!itemToEdit) return;
    
    try {
      const table = tabValue === 0 ? 'my_terms' : 'my_definitions';
      const updateField = tabValue === 0 ? 'definition' : 'custom_definition';
      
      const updateData = {
        [updateField]: editedDefinition,
        korean_definition: editedKoreanDefinition || null
      };
      
      const { error } = await supabase
        .from(table)
        .update(updateData)
        .eq('id', itemToEdit.id);
        
      if (error) throw error;
      
      setSnackbar({
        open: true,
        message: '성공적으로 수정되었습니다.',
        severity: 'success'
      });
      
      // 데이터 다시 로드
      loadData();
    } catch (error) {
      console.error('수정 실패:', error);
      setSnackbar({
        open: true,
        message: '수정 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setEditDialogOpen(false);
      setItemToEdit(null);
      setEditedDefinition('');
      setEditedKoreanDefinition('');
    }
  };
  
  // 스낵바 닫기
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`나만의 용어 (${myTerms.length})`} />
          <Tab label={`나만의 정의 (${myDefinitions.length})`} />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="검색어를 입력하세요 (용어, 정의, 키워드)"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 }
          }}
          size="small"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.paper' }}>
              <TableCell width="25%"><Typography fontWeight="bold">{tabValue === 0 ? '용어' : '원본 용어'}</Typography></TableCell>
              <TableCell width="15%"><Typography fontWeight="bold">분야</Typography></TableCell>
              <TableCell width="40%"><Typography fontWeight="bold">{tabValue === 0 ? '정의' : '나만의 정의'}</Typography></TableCell>
              <TableCell width="20%" align="center"><Typography fontWeight="bold">작업</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.length > 0 ? (
              paginatedItems.map((item) => {
                const isExpanded = expandedItem === item.id;
                
                return (
                  <React.Fragment key={item.id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {tabValue === 0 ? item.term : item.terms?.term || '삭제된 용어'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tabValue === 0 ? item.subject : item.terms?.subject}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {tabValue === 0 ? item.definition : item.custom_definition}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton 
                            size="small"
                            onClick={() => handleExpandToggle(item.id)}
                            aria-label={isExpanded ? "접기" : "펼치기"}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(item)}
                            aria-label="수정"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(item)}
                            aria-label="삭제"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell 
                        colSpan={4} 
                        sx={{ 
                          p: 0,
                          borderBottom: isExpanded ? '1px solid rgba(224, 224, 224, 1)' : 'none' 
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, backgroundColor: 'rgba(0, 0, 0, 0.01)' }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                영문 정의
                              </Typography>
                              <Typography variant="body1">
                                {tabValue === 0 ? item.definition : item.custom_definition}
                              </Typography>
                            </Box>
                            
                            {(tabValue === 0 ? item.korean_definition : item.korean_definition) && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  한국어 정의
                                </Typography>
                                <Box sx={{ 
                                  p: 2, 
                                  borderRadius: 1,
                                  backgroundColor: 'background.paper',
                                  '& a': { color: 'primary.main' }
                                }}>
                                  <ReactMarkdown>
                                    {tabValue === 0 ? item.korean_definition : item.korean_definition}
                                  </ReactMarkdown>
                                </Box>
                              </Box>
                            )}
                            
                            {(tabValue === 0 ? item.keywords : item.keywords) && 
                             (tabValue === 0 ? item.keywords.length > 0 : item.keywords.length > 0) && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  키워드
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {(tabValue === 0 ? item.keywords : item.keywords).map((keyword, idx) => (
                                    <Chip
                                      key={idx}
                                      label={keyword}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {searchQuery 
                      ? '검색 결과가 없습니다.' 
                      : tabValue === 0 
                        ? '추가한 용어가 없습니다.'
                        : '추가한 정의가 없습니다.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Pagination 
            count={pageCount} 
            page={page} 
            onChange={handlePageChange} 
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
      
      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tabValue === 0 
              ? `"${itemToDelete?.term}" 용어를 삭제하시겠습니까?` 
              : `"${itemToDelete?.terms?.term}" 용어에 대한 나만의 정의를 삭제하시겠습니까?`}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            취소
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 수정 다이얼로그 */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {tabValue === 0 
            ? `"${itemToEdit?.term}" 용어 수정` 
            : `"${itemToEdit?.terms?.term}" 용어에 대한 나만의 정의 수정`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="definition"
            label="영문 정의"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={editedDefinition}
            onChange={(e) => setEditedDefinition(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="korean-definition"
            label="한국어 정의 (선택사항)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={editedKoreanDefinition}
            onChange={(e) => setEditedKoreanDefinition(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} color="primary">
            취소
          </Button>
          <Button 
            onClick={handleEditConfirm} 
            color="primary" 
            variant="contained"
            disabled={!editedDefinition.trim()}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 알림 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyTermsList; 